'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { motion, useAnimation } from 'framer-motion';

interface IconProps {
	isHovered?: boolean;
}

const pathVariants = {
	normal: {
		y: 0,
		x: 0,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 25,
		},
	},
	animate: {
		y: -1.5,
		x: [-1, 1, -1, 1, -1, 0],
		transition: {
			y: {
				duration: 0.2,
				type: 'spring',
				stiffness: 200,
				damping: 25,
			},
			x: {
				duration: 0.3,
				repeat: Infinity,
				ease: 'linear',
			},
		},
	},
};

const secondaryPathVariants = {
	normal: {
		y: 0,
		x: 0,
		transition: {
			duration: 0.2,
			type: 'spring',
			stiffness: 200,
			damping: 25,
		},
	},
	animate: {
		y: -2.5,
		x: [-2, 2, -2, 2, -2, 0],
		transition: {
			y: {
				duration: 0.2,
				type: 'spring',
				stiffness: 200,
				damping: 25,
			},
			x: {
				duration: 0.3,
				repeat: Infinity,
				ease: 'linear',
			},
		},
	},
};

export function ClockIcon({ isHovered }: IconProps) {
	const controls = useAnimation();

	React.useEffect(() => {
		if (isHovered) {
			controls.start('animate');
		} else {
			controls.start('normal');
		}
	}, [isHovered, controls]);

	return (
		<div className='select-none p-2 flex items-center justify-center'>
			<svg
				xmlns='http://www.w3.org/2000/svg'
				width='28'
				height='28'
				viewBox='0 0 24 24'
				fill='none'
				stroke='currentColor'
				strokeWidth='2'
				strokeLinecap='round'
				strokeLinejoin='round'
				className={cn(
					'transition-colors',
					isHovered && 'text-pink-500'
				)}
			>
				<motion.path
					variants={pathVariants}
					initial='normal'
					animate={controls}
					d='M18 20.5L19.5 22'
				/>
				<motion.path
					variants={pathVariants}
					initial='normal'
					animate={controls}
					d='M6 20.5L4.5 22'
				/>
				<motion.path
					variants={pathVariants}
					initial='normal'
					animate={controls}
					d='M21 13C21 17.968 16.968 22 12 22C7.032 22 3 17.968 3 13C3 8.032 7.032 4 12 4C16.968 4 21 8.032 21 13Z'
				/>
				<motion.path
					variants={pathVariants}
					initial='normal'
					animate={controls}
					d='M15.339 15.862L12.549 14.197C12.063 13.909 11.667 13.216 11.667 12.649V8.95898'
				/>
				<motion.path
					variants={secondaryPathVariants}
					initial='normal'
					animate={controls}
					d='M18 2L21.747 5.31064'
				/>
				<motion.path
					variants={secondaryPathVariants}
					initial='normal'
					animate={controls}
					d='M6 2L2.25304 5.31064'
				/>
			</svg>
		</div>
	);
}
